import { Component } from "./component.ts";
import { claimInert, getActiveInertId, releaseInert } from "./inert-stack.ts";
import { lockScroll, unlockScroll } from "./scroll-lock.ts";

export interface PopupDialogRefs {
  overlay?: HTMLElement;

  dialog?: HTMLElement;
}

/**
 * Base class for popup dialogs (cookie banner, market prompt, etc).
 *
 * Subclasses are expected to render an overlay element with `ref="overlay"`
 * and a dialog element with `ref="dialog"`. The base class handles open/close
 * transitions, scroll lock, inert claim, and focus restoration.
 *
 * Override hooks:
 * - `inertId` — required, unique identifier for the inert claim
 * - `inertPriority` — optional, defaults to 0 (cookie banner uses 1)
 * - `getFocusTarget()` — optional, defaults to the dialog element
 * - `onEscape()` — optional, defaults to `close()`
 */
export class PopupDialog<TRefs extends PopupDialogRefs = PopupDialogRefs> extends Component<
  TRefs,
  "overlay" | "dialog"
> {
  protected requiredRefs = ["overlay", "dialog"] as const;

  #previouslyFocused: Element | null = null;

  get inertId(): string {
    throw new Error("PopupDialog subclasses must define inertId");
  }

  get inertPriority(): number {
    return 0;
  }

  getFocusTarget(): HTMLElement | null {
    return this.refs.dialog;
  }

  onEscape(): void {
    this.close();
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("keydown", this.#handleKeydown, { signal: this.signal });
  }

  disconnectedCallback(): void {
    if (this.isOpen) this.#cleanupOpenState();
    super.disconnectedCallback();
  }

  get isOpen(): boolean {
    return this.hasAttribute("data-open");
  }

  open(): void {
    if (this.isOpen) return;
    this.#previouslyFocused = document.activeElement;
    this.removeAttribute("hidden");
    void this.offsetHeight;
    this.setAttribute("data-open", "");
    lockScroll(this.inertId);
    claimInert(this.inertId, this, { priority: this.inertPriority });

    const target = this.getFocusTarget();
    if (target) target.focus({ preventScroll: true });
  }

  close(): void {
    if (!this.isOpen) return;
    this.removeAttribute("data-open");

    const cleanup = () => {
      this.setAttribute("hidden", "");
      this.#cleanupOpenState();
      if (this.#previouslyFocused instanceof HTMLElement) {
        this.#previouslyFocused.focus();
      }
      this.#previouslyFocused = null;
    };

    const dialog = this.refs.dialog;
    const fallbackTimeout = setTimeout(cleanup, 400);
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName !== "opacity") return;
      dialog.removeEventListener("transitionend", handleTransitionEnd);
      clearTimeout(fallbackTimeout);
      cleanup();
    };
    dialog.addEventListener("transitionend", handleTransitionEnd);
  }

  #cleanupOpenState(): void {
    unlockScroll(this.inertId);
    releaseInert(this.inertId);
  }

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape" || !this.isOpen) return;
    if (getActiveInertId() !== this.inertId) return;
    event.preventDefault();
    this.onEscape();
  };
}
